<script setup>
/**
 * Renderiza uma mensagem de template do WhatsApp oficial com o visual completo:
 * cabecalho (imagem ou texto), corpo formatado, rodape e botoes.
 *
 * Os valores enviados (imagem real, variaveis) vem de
 * additionalAttributes.templateParams.processedParams; a estrutura
 * (formato do header, rodape, botoes) vem da definicao do template
 * carregada na store por nome + idioma.
 */
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from '../Text/FormattedContent.vue';
import { useMessageContext } from '../../provider.js';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';

const { content, additionalAttributes, inboxId } = useMessageContext();
const { t } = useI18n();

const templatesGetter = useMapGetter('inboxes/getWhatsAppTemplates');
const selectedChat = useMapGetter('getSelectedChat');
const imageFailed = ref(false);

// inboxId nem sempre chega no contexto da mensagem (limitacao do v-bind=message),
// entao caímos no inbox da conversa selecionada — necessario pra achar a
// definicao do template (rodape/botoes/header de texto).
const resolvedInboxId = computed(
  () => inboxId.value || selectedChat.value?.inbox_id
);

const templateParams = computed(
  () => additionalAttributes.value?.templateParams || {}
);

const templateDef = computed(() => {
  const { name, language } = templateParams.value;
  if (!name) return null;
  const list = templatesGetter.value(resolvedInboxId.value) || [];
  return (
    list.find(tpl => tpl.name === name && tpl.language === language) ||
    list.find(tpl => tpl.name === name) ||
    null
  );
});

const components = computed(() => templateDef.value?.components || []);
const findComponent = type => components.value.find(c => c.type === type);

const headerComponent = computed(() => findComponent('HEADER'));
const footerComponent = computed(() => findComponent('FOOTER'));
const buttonsComponent = computed(() => findComponent('BUTTONS'));

const headerFormat = computed(() =>
  (headerComponent.value?.format || '').toUpperCase()
);

const processedHeader = computed(
  () => templateParams.value?.processedParams?.header || {}
);

const headerMediaUrl = computed(() => processedHeader.value.mediaUrl || '');

// Considera tambem o mediaType salvo no envio caso a definicao do template
// nao esteja carregada.
const isImageHeader = computed(
  () =>
    headerFormat.value === 'IMAGE' ||
    (processedHeader.value.mediaType || '').toLowerCase() === 'image'
);

const showImageHeader = computed(
  () => isImageHeader.value && headerMediaUrl.value && !imageFailed.value
);

const mediaPlaceholderIcon = computed(() => {
  if (headerFormat.value === 'VIDEO') return 'i-lucide-video';
  if (headerFormat.value === 'DOCUMENT') return 'i-lucide-file-text';
  return 'i-lucide-image';
});

const showMediaPlaceholder = computed(
  () =>
    ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(headerFormat.value) &&
    !showImageHeader.value
);

const showTextHeader = computed(
  () => headerFormat.value === 'TEXT' && !!headerComponent.value?.text
);

const templateButtons = computed(() => buttonsComponent.value?.buttons || []);

const buttonIcon = button => {
  if (button.type === 'URL') return 'i-lucide-external-link';
  if (button.type === 'PHONE_NUMBER') return 'i-lucide-phone';
  if (button.type === 'COPY_CODE') return 'i-lucide-copy';
  return 'i-lucide-reply';
};

const buttonLabel = button => {
  if (button.type === 'COPY_CODE') {
    return t('CONVERSATION.WHATSAPP_TEMPLATE_COPY_CODE');
  }
  return button.text;
};

// Carrossel
const carouselComponent = computed(() => findComponent('CAROUSEL'));
const isCarousel = computed(() => !!carouselComponent.value);
const carouselCards = computed(() => {
  const defCards = carouselComponent.value?.cards || [];
  const sentCards =
    templateParams.value?.processedParams?.carousel?.cards || [];
  return defCards.map((card, index) => {
    const cardComponents = card.components || [];
    const header = cardComponents.find(c => c.type === 'HEADER');
    return {
      mediaUrl:
        sentCards[index]?.mediaUrl || header?.example?.header_handle?.[0] || '',
      body: cardComponents.find(c => c.type === 'BODY')?.text || '',
      buttons: cardComponents.find(c => c.type === 'BUTTONS')?.buttons || [],
    };
  });
});

// Oferta por tempo limitado (LTO)
const ltoComponent = computed(() => findComponent('LIMITED_TIME_OFFER'));
const isLto = computed(() => !!ltoComponent.value);
const ltoText = computed(
  () => ltoComponent.value?.limited_time_offer?.text || ''
);
</script>

<template>
  <BaseBubble class="overflow-hidden" data-bubble-name="whatsapp-template">
    <span
      class="block px-[13px] pt-[9px] text-[10px] uppercase tracking-wider opacity-60 font-medium"
    >
      {{ t('CONVERSATION.WHATSAPP_TEMPLATE_LABEL') }}
    </span>

    <!-- Carrossel -->
    <template v-if="isCarousel">
      <div class="px-[13px] py-[9px]">
        <FormattedContent v-if="content" :content="content" />
      </div>
      <div class="flex gap-2 px-[13px] pb-[9px] overflow-x-auto">
        <div
          v-for="(card, index) in carouselCards"
          :key="`card-${index}`"
          class="w-40 shrink-0 rounded-lg overflow-hidden bg-white dark:bg-n-solid-2 shadow-sm"
        >
          <img
            v-if="card.mediaUrl"
            :src="card.mediaUrl"
            class="object-cover w-full h-24"
            alt=""
          />
          <div
            v-else
            class="flex justify-center items-center w-full h-24 bg-black/5 dark:bg-white/10"
          >
            <span class="opacity-60 i-lucide-image size-6" />
          </div>
          <p
            class="px-2 py-1.5 text-xs text-n-slate-12 break-words leading-snug"
          >
            {{ card.body }}
          </p>
          <div
            v-if="card.buttons.length"
            class="border-t border-black/10 dark:border-white/10"
          >
            <div
              v-for="(button, bidx) in card.buttons"
              :key="`cbtn-${bidx}`"
              class="flex gap-1 justify-center items-center py-1.5 text-xs font-medium text-[#00a5f4]"
              :class="{
                'border-t border-black/10 dark:border-white/10': bidx > 0,
              }"
            >
              <span :class="buttonIcon(button)" class="size-3" />
              {{ buttonLabel(button) }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Padrão / LTO -->
    <template v-else>
      <!-- Selo de oferta por tempo limitado -->
      <div
        v-if="isLto"
        class="flex gap-1.5 items-center px-[13px] py-1.5 text-xs font-semibold bg-black/5 dark:bg-white/10"
      >
        <span class="i-lucide-clock size-3.5" />
        {{ ltoText }}
      </div>

      <!-- Cabecalho de imagem (URL real enviada) -->
      <img
        v-if="showImageHeader"
        :src="headerMediaUrl"
        class="object-cover mt-1.5 w-full max-h-72"
        alt=""
        @error="imageFailed = true"
      />
      <!-- Cabecalho de midia sem preview (video/documento ou imagem indisponivel) -->
      <div
        v-else-if="showMediaPlaceholder"
        class="flex justify-center items-center mt-1.5 mx-[13px] h-24 rounded-lg bg-black/5 dark:bg-white/10"
      >
        <span :class="mediaPlaceholderIcon" class="opacity-60 size-8" />
      </div>

      <div class="px-[13px] py-[9px] flex flex-col gap-1.5">
        <!-- Cabecalho de texto -->
        <p v-if="showTextHeader" class="font-semibold">
          {{ headerComponent.text }}
        </p>
        <!-- Corpo (ja renderizado com as variaveis) -->
        <FormattedContent v-if="content" :content="content" />
        <!-- Rodape -->
        <p v-if="footerComponent" class="text-xs opacity-60">
          {{ footerComponent.text }}
        </p>
      </div>

      <!-- Botoes -->
      <div
        v-if="templateButtons.length"
        class="border-t border-black/10 dark:border-white/10"
      >
        <div
          v-for="(button, index) in templateButtons"
          :key="`btn-${index}`"
          class="flex gap-1.5 justify-center items-center py-2 text-sm font-medium text-[#00a5f4]"
          :class="{
            'border-t border-black/10 dark:border-white/10': index > 0,
          }"
        >
          <span :class="buttonIcon(button)" class="size-3.5" />
          {{ buttonLabel(button) }}
        </div>
      </div>
    </template>
  </BaseBubble>
</template>
